import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { api, apiErrorMessage, isApiError } from "@/api/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Shield,
  Download,
  QrCode,
  LogOut,
  Plus,
  Loader2,
  Copy,
  Check,
  Wifi,
  Trash2,
  LayoutDashboard,
  Globe,
  Pencil,
  BookOpen,
  Mail,
  X,
} from "lucide-react";
import { GeositeCatalog } from "@/components/GeositeCatalog";
import { QrDialog } from "@/components/QrDialog";

interface Config {
  id: number;
  type: string;
  name: string;
  address?: string;
  public_key?: string;
  config_text?: string;
  created_at: string;
}

type UserTab = "configs" | "routes";

export function UserDashboard() {
  const { user, setUser, logout } = useAuth();
  const { toast } = useToast();
  const [configs, setConfigs] = useState<Config[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);
  const [qrOpen, setQrOpen] = useState<{ id: number; title: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [activeTab, setActiveTab] = useState<UserTab>("configs");

  const [emailInput, setEmailInput] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem("emailBannerDismissed") === "true";
    } catch {
      return false;
    }
  });

  const loadConfigs = async () => {
    try {
      const res = await api.get("/api/configs");
      setConfigs(res.data.configs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    api
      .get("/api/configs")
      .then((res) => {
        if (mounted) setConfigs(res.data.configs || []);
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/api/configs/wg");
      await loadConfigs();
    } catch (e: unknown) {
      const errMsg = apiErrorMessage(e, "");
      if (isApiError(e) && e.response?.status === 429) {
        toast("Слишком частые запросы. Попробуйте через минуту.");
      } else if (
        errMsg.toLowerCase().includes("limit") ||
        errMsg.includes("макс") ||
        errMsg.includes("лимит")
      ) {
        toast("Достигнут лимит конфигураций (макс. 5)");
      } else {
        toast(errMsg || "Ошибка генерации");
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyConfig = (text: string, id: number) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const deleteConfig = async (id: number) => {
    if (!confirm("Удалить конфиг?")) return;
    try {
      await api.post(`/api/configs/wg/${id}/delete`);
      await loadConfigs();
    } catch (e: unknown) {
      toast(apiErrorMessage(e, "Ошибка удаления"));
    }
  };

  const startEditing = (cfg: Config) => {
    setEditingId(cfg.id);
    setEditingName(cfg.name || "");
  };

  const saveRename = async (cfg: Config) => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    const previousName = cfg.name;
    try {
      const res = await api.post(`/api/configs/wg/${cfg.id}/rename`, { name: trimmed });
      if (!res.data.success) {
        throw new Error(res.data.error || "Ошибка переименования");
      }
      await loadConfigs();
    } catch (e: unknown) {
      console.error("Rename failed:", e);
      toast(apiErrorMessage(e, "Ошибка переименования"));
      setEditingName(previousName);
    } finally {
      setEditingId(null);
    }
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, cfg: Config) => {
    if (e.key === "Enter") {
      saveRename(cfg);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const saveEmail = async () => {
    if (!emailInput.trim()) return;
    setEmailLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("email", emailInput.trim());
      const res = await api.post("/api/user/email", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data.success) {
        toast("Письмо с подтверждением отправлено", "success");
        setEmailInput("");
        setEmailEditMode(false);
        const meRes = await api.get("/api/auth/me");
        if (meRes.data.authenticated && meRes.data.user) {
          setUser(meRes.data.user);
        }
      } else {
        toast(res.data.error || "Ошибка сохранения email");
      }
    } catch (e: unknown) {
      toast(apiErrorMessage(e, "Ошибка сохранения email"));
    } finally {
      setEmailLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!user?.email) return;
    setEmailLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append("email", user.email);
      const res = await api.post("/api/user/email", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data.success) {
        toast("Письмо с подтверждением отправлено", "success");
      } else {
        toast(res.data.error || "Ошибка отправки");
      }
    } catch (e: unknown) {
      toast(apiErrorMessage(e, "Ошибка отправки"));
    } finally {
      setEmailLoading(false);
    }
  };

  const wgConfigs = configs.filter((cfg) => cfg.type === "wg");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-6 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-emerald-400" />
            <h1 className="text-xl font-bold">Secure Access Gateway</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/instructions">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <BookOpen className="mr-2 h-4 w-4" />
                Инструкция
              </Button>
            </Link>
            {user?.is_admin && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Админ-панель
                </Button>
              </Link>
            )}
            <span className="text-sm text-slate-400">{user?.username}</span>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-3xl mx-auto">
        {!user?.email && !bannerDismissed && (
          <div className="mb-6 rounded-lg bg-amber-500/10 border border-amber-500/20 p-4 flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm text-amber-200">
                Укажите email в профиле, чтобы иметь возможность входить без пароля и восстанавливать доступ, если забудете пароль.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-300 hover:text-amber-100 hover:bg-amber-500/20 shrink-0"
              onClick={() => {
                document.getElementById("email-card")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Указать email
            </Button>
            <button
              onClick={() => {
                setBannerDismissed(true);
                try {
                  localStorage.setItem("emailBannerDismissed", "true");
                } catch {
                  // ignore
                }
              }}
              className="text-amber-300 hover:text-amber-100 p-1 shrink-0"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Tab tiles */}
        <div className="grid gap-4 grid-cols-2 mb-6">
          <Card
            className={`border-slate-800 bg-slate-900 cursor-pointer transition-all hover:border-slate-600 ${
              activeTab === "configs" ? "ring-1 ring-emerald-500/50 border-emerald-500/30" : ""
            }`}
            onClick={() => setActiveTab("configs")}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-300">Конфиги</CardTitle>
              <Wifi className={`h-4 w-4 ${activeTab === "configs" ? "text-emerald-400" : "text-slate-400"}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{wgConfigs.length}</div>
              <div className="text-xs text-slate-500">WireGuard конфигураций</div>
            </CardContent>
          </Card>

          <Card
            className={`border-slate-800 bg-slate-900 cursor-pointer transition-all hover:border-slate-600 ${
              activeTab === "routes" ? "ring-1 ring-emerald-500/50 border-emerald-500/30" : ""
            }`}
            onClick={() => setActiveTab("routes")}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-300">Маршруты</CardTitle>
              <Globe className={`h-4 w-4 ${activeTab === "routes" ? "text-emerald-400" : "text-slate-400"}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">Geosite</div>
              <div className="text-xs text-slate-500">Категории маршрутизации</div>
            </CardContent>
          </Card>
        </div>

        {/* Email Card */}
        <Card id="email-card" className="border-slate-800 bg-slate-900 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!user?.email ? (
              <div className="space-y-2">
                <p className="text-sm text-slate-400">Email не указан</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-slate-100"
                  />
                  <Button
                    size="sm"
                    onClick={saveEmail}
                    disabled={emailLoading || !emailInput.trim()}
                  >
                    {emailLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Сохранить
                  </Button>
                </div>
              </div>
            ) : emailEditMode ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  placeholder="Email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={saveEmail}
                    disabled={emailLoading || !emailInput.trim()}
                  >
                    {emailLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Сохранить
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEmailEditMode(false);
                      setEmailInput("");
                    }}
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-slate-200">{user.email}</div>
                  {user.email_verified ? (
                    <div className="text-xs text-emerald-400 mt-0.5">подтвержден</div>
                  ) : (
                    <div className="text-xs text-amber-400 mt-0.5">не подтвержден</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {!user.email_verified && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={resendVerification}
                      disabled={emailLoading}
                    >
                      {emailLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Отправить письмо повторно
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEmailEditMode(true);
                      setEmailInput(user.email || "");
                    }}
                  >
                    {user.email_verified ? "Изменить email" : "Изменить"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {activeTab === "routes" && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-slate-400" />
              Маршруты
            </h2>
            <GeositeCatalog />
          </div>
        )}

        {activeTab === "configs" && (
          <>
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Wifi className="h-5 w-5 text-emerald-400" />
                Мои конфиги
              </h2>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-700"
                onClick={generate}
                disabled={generating}
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Создать конфиг
              </Button>
            </div>

            {loading ? (
              <div className="text-center text-slate-400 py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                Загрузка...
              </div>
            ) : wgConfigs.length === 0 ? (
              <div className="text-center text-slate-500 py-12 border border-dashed border-slate-800 rounded-lg">
                <Plus className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Нет конфигураций</p>
                <p className="text-sm mt-1">Нажмите + для создания</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wgConfigs.map((cfg) => (
                  <Card
                    key={cfg.id}
                    className="border-slate-800 bg-slate-900 hover:border-slate-700 transition-colors"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wifi className="h-4 w-4 text-emerald-400" />
                          {editingId === cfg.id ? (
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onBlur={() => saveRename(cfg)}
                              onKeyDown={(e) => handleRenameKeyDown(e, cfg)}
                              autoFocus
                              className="h-7 text-sm bg-slate-800 border-slate-700 text-slate-100 w-48"
                            />
                          ) : (
                            <>
                              <CardTitle className="text-base">
                                {cfg.name || `AWG — ${cfg.address}`}
                              </CardTitle>
                              <button
                                onClick={() => startEditing(cfg)}
                                className="text-slate-500 hover:text-slate-300 transition-colors"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(cfg.created_at).toLocaleDateString("ru-RU")}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-2">
                      <a href={`/api/config/wg/${cfg.id}`} download={`awg-${cfg.id}.conf`}>
                        <Button variant="outline" size="sm">
                          <Download className="mr-2 h-4 w-4" />
                          Скачать
                        </Button>
                      </a>
                      <Button variant="outline" size="sm" onClick={() => setQrOpen({ id: cfg.id, title: cfg.name || `AmneziaWG — ${cfg.address || "config"}` })}>
                        <QrCode className="mr-2 h-4 w-4" />
                        QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyConfig(cfg.config_text || "", cfg.id)}
                      >
                        {copied === cfg.id ? (
                          <Check className="mr-2 h-4 w-4 text-emerald-400" />
                        ) : (
                          <Copy className="mr-2 h-4 w-4" />
                        )}
                        {copied === cfg.id ? "Скопировано" : "Копировать"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-400 border-red-900/50 hover:bg-red-900/20"
                        onClick={() => deleteConfig(cfg.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Удалить
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
        {qrOpen && (
          <QrDialog
            configId={qrOpen.id}
            title={qrOpen.title}
            open={!!qrOpen}
            onClose={() => setQrOpen(null)}
          />
        )}
      </main>
    </div>
  );
}
